/**
 * @fileoverview Audio component for rendering audio player with recording functionality
 *
 * @example
 * // Usage in component:
 * import { AudioQuestion } from './Audio'
 *
 * <AudioQuestion onSubmit={onSubmit} question={question} errors={errors} />
 */

import { useContext, useEffect, useState } from 'react'
import { MiddlewareContext } from '../../MiddlewareContext'
import { DesktopProgress, MobileProgress } from '../Progress/Progress'
import { StreamingRecorder, StreamingState } from '../StreamingRecorder/StreamingRecorder'
import { Text } from '../typography/Typography'
import css from './Audio.module.css'
import { Instructions } from './sections/Instructions'
import { Title } from './sections/Title'

/**
 * @function getTitle
 * @description Helper function to get the title based on the streaming state
 * @param {Novoic.AudioQuestionBody} body - The body of the audio question
 * @param {StreamingState} state - The current streaming state
 * @returns {string} The title to display
 */
function getTitle(body: Novoic.AudioQuestionBody, state: StreamingState): string {
    switch (state) {
        case StreamingState.IDLE:
        case StreamingState.LOADING:
            return body.title
        case StreamingState.PLAYING:
            return body.titlePlaying
        case StreamingState.STREAMING:
            return body.titleRecording
        case StreamingState.PROCESSING:
        default:
            return ''
    }
}

/**
 * @function AudioQuestion
 * @description Component for rendering audio player with recording functionality
 * @param {Novoic.AudioQuestionProps} props - The props for the component
 * @returns {JSX.Element} The AudioQuestion component
 */
export function AudioQuestion({ onSubmit, question, errors }: Novoic.AudioQuestionProps) {
    const { id, question: body } = question
    const {
        instructions,
        subtext,
        audio,
        recordingStartMode,
        recordingStopMode,
        maxRecordingTime,
    } = body

    const [attempt, setAttempt] = useState<number>(0)
    const { exit } = useContext(MiddlewareContext)
    const [state, setState] = useState<StreamingState>(StreamingState.IDLE)
    const params = new URLSearchParams(window.location.search)

    /**
     * Submit when streaming is finished
     */
    function onStreamingEnded(recording: string) {
        onSubmit(id, { recording })
    }

    /**
     * Change the state of the streaming so UI can be updated based on it
     */
    function onStateChange(state: StreamingState) {
        setState(state)
    }

    /**
     * Reset the state of the audio question component
     */
    useEffect(() => {
        if (errors && Object.keys(errors).length > 0) {
            setState(StreamingState.IDLE)
            setAttempt(attempt + 1)
        }
    }, [errors])

    return (
        <div className={css.container}>
            <MobileProgress />
            <Title flexible>{getTitle(body, state)}</Title>
            {subtext && <Text>{subtext}</Text>}
            {instructions && <Instructions instructions={instructions} />}

            <StreamingRecorder
                attempt={attempt}
                skippable={params.get('debug') === 'true'}
                onStreamingEnded={onStreamingEnded}
                onStateChange={onStateChange}
                onExit={exit}
                audio={audio}
                recordingStartMode={recordingStartMode}
                recordingStopMode={recordingStopMode}
                maxRecordingTime={maxRecordingTime}
            />
            <div className={css.progress}>
                <DesktopProgress />
            </div>
        </div>
    )
}
